import { InjectionToken } from "@angular/core";
import type { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import type { AccountInfo, AuthenticationResult, EventMessage, HandleRedirectPromiseOptions, InteractionStatus, RedirectRequest, SilentRequest, SsoSilentRequest } from "@azure/msal-browser";
import type { Observable } from "rxjs";

// Schmale Sicht auf MSAL, ausschließlich mit dem, was die Anwendung tatsächlich benutzt.
//
// Zweck: MSAL aus dem initialen Bundle heraushalten. Vorher injizierten MsalB2CService und
// beide UserAuthorizationService die MSAL-Klassen direkt im Konstruktor - damit lag
// @azure/msal-browser samt @azure/msal-angular im initialen Graphen (gemessen 323 kB roh in
// 132 Modulen), unabhängig davon, ob der Besucher angemeldet ist. Ein anonymer Besucher und
// jeder Crawler lädt das ohne Not.
//
// Über diese Tokens hängen die Dienste nur noch an einer Schnittstelle. Bereitgestellt wird
// je nach Bedarf ein MSAL-Adapter (dynamisch geladen) oder eine No-Auth-Fassung.
//
// Alle MSAL-Typen hier sind `import type` und werden beim Kompilieren entfernt - sie erzeugen
// keinen Verweis auf das Paket.

export interface IAuthClient {
	initialize(): Observable<void>;
	handleRedirect(options?: HandleRedirectPromiseOptions): Observable<AuthenticationResult | null>;
	loginRedirect(request: RedirectRequest): Observable<void>;
	logoutRedirect(): Observable<void>;
	ssoSilent(request: SsoSilentRequest): Observable<AuthenticationResult>;
	acquireTokenSilent(request: SilentRequest): Observable<AuthenticationResult>;
	acquireTokenPopup(request: SilentRequest): Observable<AuthenticationResult>;
	getAllAccounts(): AccountInfo[];

	/**
	 * Prüft, ob ein Fehler eine erneute Benutzerinteraktion verlangt.
	 * Liegt hier und nicht beim Aufrufer, weil die Prüfung ein `instanceof
	 * InteractionRequiredAuthError` ist - also einen Wertzugriff auf msal-browser braucht.
	 */
	isInteractionRequired(error: unknown): boolean;

	/** Meldet, ob echtes MSAL dahintersteht. Die No-Auth-Fassung gibt false zurück. */
	readonly isAuthenticationAvailable: boolean;
}

export interface IAuthEvents {
	events$: Observable<EventMessage>;
	progress$: Observable<InteractionStatus>;
}

/**
 * Routenschutz für Bereiche, die eine Anmeldung verlangen.
 *
 * Über ein eigenes Token, damit routes.ts nicht MsalGuard importieren muss - routes.ts wird
 * von main.ts statisch geladen, ein MsalGuard-Import dort hielt @azure/msal-angular im
 * initialen Bundle. Im MSAL-Zweig zeigt das Token auf MsalGuard, im No-Auth-Zweig auf eine
 * Fassung, die die Anmeldung anstößt.
 */
export interface IAuthRouteGuard {
	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean;
}

export const AUTH_CLIENT = new InjectionToken<IAuthClient>("ADE_AUTH_CLIENT");
export const AUTH_EVENTS = new InjectionToken<IAuthEvents>("ADE_AUTH_EVENTS");
export const AUTH_ROUTE_GUARD = new InjectionToken<IAuthRouteGuard>("ADE_AUTH_ROUTE_GUARD");

// Ereignis- und Statuswerte als Literale.
//
// EventType und InteractionStatus sind in msal-browser Const-Objekte, ihre Mitglieder also
// Zeichenketten. Sie hier zu spiegeln vermeidet einen Wertimport aus @azure/msal-browser in
// eifrig erreichbarem Code - genau der hielt das Paket im initialen Bundle. Die Typen bleiben
// über `import type` erhalten, der Vergleich mit `subject.eventType` ist also weiter geprüft.
//
// Nicht gespiegelt wird InteractionType: das ist ein echtes TypeScript-enum und damit
// Laufzeitcode. Es wird nur in den MSAL-Providern gebraucht, wo MSAL ohnehin geladen ist.
export const AUTH_EVENT_LOGIN_SUCCESS = "msal:loginSuccess";
export const AUTH_EVENT_LOGOUT_SUCCESS = "msal:logoutSuccess";
export const AUTH_EVENT_ACQUIRE_TOKEN_FAILURE = "msal:acquireTokenFailure";

export const AUTH_PROGRESS_NONE = "none" as InteractionStatus;
export const AUTH_PROGRESS_STARTUP = "startup" as InteractionStatus;
